import React, { useState } from "react";
import { QualifiedFeatureFragment } from "fe-common/graphql";
import { Graph, SolverFeatureConnection } from "../solver/useSolver";
import { UserStorageKey } from "fe-web-components/userStorage/userStorageKey";
import {
  readFromGlobalStorage,
  writeToGlobalStorage,
} from "fe-web-components/userStorage/globalUserStorage";

//#region API
export type FeatureQuery = {
  featureId: string;
  featureData: QualifiedFeatureFragment;
  color: string;
};

export type SearchResultDisplayData = {
  icon: string;
};

export type FeatureSearchResult = {
  featureId: string;
  // Data Level
  featureData: SolverFeatureConnection;
  // Display Data level
  featureDisplayData?: SearchResultDisplayData;
};

export type SearchResult = {
  features: string[];
  featureSearchResult: FeatureSearchResult[];
};

export type SearchOptions = {
  order: string;
};
export interface AskKahunSearchQueriesApi {
  features: FeatureQuery[];
  updateFeaturesList: (features: FeatureQuery[]) => any;
  options: SearchOptions;
  updateOptions: (options: SearchOptions) => any;
  searchResults: SearchResult;
  updateSearchResults: (results: SearchResult) => any;
  graph: Graph | null;
  updateGraph: (graph: Graph | null) => any;
  agreeOnTerms: boolean;
  setAgreeOnTerms: (agree: Boolean) => void;
}
//#endregion

//#region Hook
export const useSearchQueriesContext = () => {
  const [features, setFeaturesList] = useState<FeatureQuery[]>([]);
  const [searchResults, setSearchResults] = useState<SearchResult>({
    features: [],
    featureSearchResult: [],
  });
  const [graph, setGraph] = useState<Graph | null>(null);
  const [options, setOptions] = useState<SearchOptions>({ order: "asc" });
  const [agreeOnTerms, setAgreeOnTermsSate] = useState<Boolean>(
    readFromGlobalStorage(UserStorageKey.UserAgreement) as Boolean,
  );

  const updateFeaturesList = React.useCallback(
    (features) => setFeaturesList(features),
    [],
  );

  const updateSearchResults = React.useCallback(
    (results) => setSearchResults(results),
    [],
  );

  const setAgreeOnTerms = React.useCallback(
    (value: boolean) => {
      writeToGlobalStorage(
        UserStorageKey.UserAgreement,
        value ? "true" : "false",
      );
      setAgreeOnTermsSate(value);
    },
    [setAgreeOnTermsSate],
  );

  const updateGraph = React.useCallback((g) => setGraph(g), []);
  const updateOptions = React.useCallback((options) => setOptions(options), []);
  return {
    features,
    updateFeaturesList,
    options,
    updateOptions,
    searchResults,
    updateSearchResults,
    graph,
    updateGraph,
    agreeOnTerms,
    setAgreeOnTerms,
  } as AskKahunSearchQueriesApi;
};
//#endregion

const mockUpdateFunction = (functionName: string) => () => {
  console.error(`default ${functionName} was called no data will be changed`);
};
export const SearchQueriesDefault: AskKahunSearchQueriesApi = {
  features: [],
  options: { order: "asc" },
  updateFeaturesList: mockUpdateFunction("updateFeaturesList"),
  updateOptions: mockUpdateFunction("updateOptions"),
  searchResults: { features: [], featureSearchResult: [] },
  updateSearchResults: mockUpdateFunction("updateSearchResults"),
  graph: null,
  updateGraph: mockUpdateFunction("updateGraph"),
  agreeOnTerms: true,
  setAgreeOnTerms: mockUpdateFunction("setAgreeOnTerms"),
};

export const SearchQueriesContext = React.createContext(SearchQueriesDefault);
